import { Children, type ReactNode, useCallback, useMemo } from "react";
import { Theme } from "tamagui";
import type { ContainerLayoutProps } from "../../utils/layout";
import type { SelectionTone } from "../Card/SelectionCard";
import { View, type ViewProps, XStack } from "../Stacks";
import { SelectionGroupContext } from "./SelectionContext";
import { SelectionRow } from "./SelectionRow";
import { SelectionTile } from "./SelectionTile";
type SelectionGroupBaseProps = {
  disabled?: boolean;
  showSelector?: boolean;
  /** Overrides each item's tone. Unset, `prominent` rows take `brand` and everything else `default`. */
  tone?: SelectionTone;
  label?: string;
  children: ReactNode;
} & ContainerLayoutProps & Pick<ViewProps, "width">;
type SelectionGroupGridProps = {
  columns: number;
  flexDirection?: never;
  flexWrap?: never;
  columnGap?: never;
} | {
  columns?: never;
};
type SelectionGroupProps = SelectionGroupBaseProps & SelectionGroupGridProps & ({
  mode: "radio";
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
} | {
  mode: "checkbox" | "switch";
  value: string[];
  onChange: (value: string[]) => void;
  required?: never;
});

// Fixed rows rather than one wrapping container: a percentage flexBasis can't
// subtract its share of the gap without `calc()`, which is web-only.
const toGridRows = (children: ReactNode, columns: number, gap: ContainerLayoutProps["gap"]): ReactNode[] => {
  // A zero or negative count never advances the loop below, so fail loudly.
  if (!Number.isInteger(columns) || columns < 1) {
    throw new Error(`SelectionGroup: columns must be a positive integer, got ${columns}.`);
  }
  const items = Children.toArray(children);
  const rows: ReactNode[] = [];
  for (let start = 0; start < items.length; start += columns) {
    rows.push(<XStack key={start} gap={gap}>
        {/* Empty cells pad a ragged row; zero basis stops tile padding skewing columns. */}
        {Array.from({
        length: columns
      }, (_, index) => <View key={index} flex={1} flexBasis={0}>
            {items[start + index]}
          </View>)}
      </XStack>);
  }
  return rows;
};
const SelectionGroupRoot = ({
  mode,
  value,
  onChange,
  disabled = false,
  showSelector = false,
  tone,
  label,
  required,
  children,
  columns,
  flexDirection = "column",
  gap = "$sp1.5",
  ...layoutProps
}: SelectionGroupProps) => {
  const isSelected = useCallback((itemValue: string): boolean => {
    if (mode === "radio") {
      return value === itemValue;
    }
    return Array.isArray(value) && value.includes(itemValue);
  }, [mode, value]);
  const onSelect = useCallback((itemValue: string) => {
    if (mode === "radio") {
      (onChange as (v: string) => void)(itemValue);
    } else {
      const next = value.includes(itemValue) ? value.filter(v => v !== itemValue) : [...value, itemValue];
      (onChange as (v: string[]) => void)(next);
    }
  }, [mode, value, onChange]);
  const contextValue = useMemo(() => ({
    mode,
    isSelected,
    onSelect,
    disabled,
    showSelector,
    tone
  }), [mode, isSelected, onSelect, disabled, showSelector, tone]);
  return <SelectionGroupContext.Provider value={contextValue} data-sentry-element="SelectionGroupContext.Provider" data-sentry-component="SelectionGroupRoot" data-sentry-source-file="SelectionGroup.tsx">
      <Theme name="v2" data-sentry-element="Theme" data-sentry-source-file="SelectionGroup.tsx">
        <View flexDirection={flexDirection} gap={gap} {...layoutProps} role={mode === "radio" ? "radiogroup" : "group"} aria-label={label} aria-required={required ? true : undefined} data-sentry-element="View" data-sentry-source-file="SelectionGroup.tsx">
          {columns === undefined ? children : toGridRows(children, columns, gap)}
        </View>
      </Theme>
    </SelectionGroupContext.Provider>;
};
type SelectionGroupComposition = {
  Row: typeof SelectionRow;
  Tile: typeof SelectionTile;
};
export const SelectionGroup = Object.assign(SelectionGroupRoot, {
  Row: SelectionRow,
  Tile: SelectionTile
}) as typeof SelectionGroupRoot & SelectionGroupComposition;
export type { SelectionGroupProps };